import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query, state} from "lit/decorators.js";
import {type Job, JobLocationsRequest, JobPreviewRequest, JobService} from "./jobService";
import {schedule} from "../../common/utils/promises";
import {EopSpinner, EopViewportSpinner} from "../../page/elements/spinner";
import {resolve} from "../../container";
import "../../page/elements/input/dropdown";
import {Dictionary} from "../../page/elements/dictionary";
import type {Nullable} from "../../bootstrap/common/types";
import {DROPDOWN_EVENT, type DropdownEvent} from "../../page/elements/input/inputEvents";
import type {DataSelectOption, EopDropdown} from "../../page/elements/input/dropdown";
import {trackingButtonInteract, trackingTextlinkInteract} from "../../tracking/trackingAttributes";
import {UnLitElement, WithEopSlots} from "../../common/elements";
import {prepareEvents} from "../../common/utils/events";
import {fresh} from "../../common/lifetime";


type LabeledUserFilterBase = {
    label: string;
    inverseFilter: boolean;
}

type ConfiguredUserFilter = LabeledUserFilterBase & {
    filter: Filter;
    inverseFilter: false;
};

type OtherUserFilter = LabeledUserFilterBase & {
    inverseFilter: true;
};

type LabeledUserFilter = ConfiguredUserFilter | OtherUserFilter;

type Filter = string[]

const parseLabeledUserFilters = (jsonString: Nullable<string>): LabeledUserFilter[] => {
    if (!jsonString) {
        return [];
    }

    return jsonString.parseAsJSON<{ label: string, value: string }[]>()
        .map(item => ({
            label: item.label,
            filter: item.value.parseCommaSeparated(),
            inverseFilter: false
        }));
};

const parseCommaSeparated = (commaSeparatedString: Nullable<string>): string[] => {
    return commaSeparatedString?.parseCommaSeparated() ?? [];
};

const dataSelectOption = (label: string): DataSelectOption => {
    return {
        label: label,
        value: label,
        preset: false
    };
};


@customElement("eop-job-list-preview")
export class EopJobListPreview extends WithEopSlots(UnLitElement) {

    @property({attribute: "limit", type: Number})
    private limit: number;
    @property({attribute: "filter-job-types", converter: parseCommaSeparated})
    private typeFilter: Filter = [];
    @property({attribute: "filter-job-locations", converter: parseCommaSeparated})
    private locationFilter: Filter = [];
    @property({attribute: "job-locations-filter-mode"})
    private locationsFilterMode: "none" | "prefilter" | "static_dropdown" | "dynamic_dropdown" = "none";
    @property({attribute: "filter-job-companies", converter: parseCommaSeparated})
    private companiesFilter: Filter = [];
    @property({attribute: "pre-filter-job-titles", converter: parseCommaSeparated})
    private titlePreFilter: Filter = [];
    @property({attribute: "user-filter-job-titles", converter: parseLabeledUserFilters})
    private titleUserFilters: LabeledUserFilter[] = [];
    @property({attribute: "intro-text-slot-name"})
    private introTextSlotName: string;
    @property({attribute: "empty-results-text-slot-name"})
    private emptyResultsTextSlotName: string;

    @state()
    private jobs: Job[] = [];
    @state()
    private offset: number = 0;
    @state()
    private total: Nullable<number> = null;

    @query("eop-spinner")
    protected fetchSpinner: EopSpinner;
    @query("eop-viewport-spinner")
    protected fetchMoreSpinner: EopViewportSpinner;

    private currentLocationUserFilter: Nullable<Filter> = null;
    private currentTitleUserFilter: Nullable<LabeledUserFilter> = null;
    private unionOfTitleUserFilters: Filter = [];
    private dictionary: Dictionary;
    private labelOtherTitle: string;
    private checkedForOtherJobs: boolean = false;

    public constructor(private jobService: JobService = resolve(JobService)) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
        this.labelOtherTitle = this.dictionary.translate("JOB_PREVIEW_DROPDOWN_OTHER_OPTION");
        this.unionOfTitleUserFilters = this.titleUserFilters
            .filter(filter => !filter.inverseFilter)
            .flatMap(filter => filter.filter)
            .distinct();

        if (!this.checkedForOtherJobs && this.unionOfTitleUserFilters.length > 0) {
            void schedule(this.extendTitleDropdownOptionsByOtherJobs())
                .as("check other jobs");
        }

        if (this.locationsFilterMode === "dynamic_dropdown") {
            void schedule(this.loadAvailableLocations())
                .as("check available locations");
        }
    }

    private async extendTitleDropdownOptionsByOtherJobs(): Promise<void> {
        const response = await this.jobService.jobs(
            new JobPreviewRequest(
                this.typeFilter,
                this.locationFilter,
                this.companiesFilter,
                this.titlePreFilter,
                this.unionOfTitleUserFilters,
                0,
                0,
                true
            )
        );

        if (response.total > 0) {
            const DROPDOWN_OTHER_OPTION: LabeledUserFilter = {
                label: this.labelOtherTitle,
                inverseFilter: true
            };
            this.titleUserFilters = this.titleUserFilters.concat(DROPDOWN_OTHER_OPTION);
        }

        this.checkedForOtherJobs = true;
    }

    private async loadAvailableLocations(): Promise<void> {
        const response = await this.jobService.locations(
            new JobLocationsRequest(this.typeFilter, this.companiesFilter, this.titlePreFilter)
        );

        if (!response.locations.isEmpty()) {
            this.locationFilter = response.locations;
        }
    }

    public render(): TemplateResult {
        return html`
            ${this.renderIntroText()}
            ${this.renderDropdowns()}
            <eop-viewport-spinner></eop-viewport-spinner>
            <eop-spinner></eop-spinner>
            ${this.renderResultsArea()}
            ${this.renderLoadMoreButton()}
        `;
    }

    private renderIntroText(): Nullable<TemplateResult> {
        if (this.total === 0) {
            return null;
        }
        return html`
            <div class="intro-text">
                ${this.resolveSlot(this.introTextSlotName)}
            </div>
        `;
    }

    private renderDropdowns(): Nullable<TemplateResult> {
        let locationDropdown: Nullable<TemplateResult> = null;
        let titleDropdown: Nullable<TemplateResult> = null;
        const locationDropdownPlaceholder = this.dictionary.translate("JOB_PREVIEW_LOCATION_DROPDOWN_PLACEHOLDER");
        const titleDropdownPlaceholder = this.dictionary.translate("JOB_PREVIEW_TITLE_DROPDOWN_PLACEHOLDER");
        const showLocationsDropdown = this.locationsFilterMode === "static_dropdown" || this.locationsFilterMode === "dynamic_dropdown";

        if (showLocationsDropdown && this.locationFilter.length > 1) {
            locationDropdown = this.renderDropdown("job-locations",
                this.dictionary.translate("JOB_PREVIEW_LOCATION_DROPDOWN"),
                locationDropdownPlaceholder,
                this.locationFilter.map(location => dataSelectOption(location))
            );
        }

        if (this.unionOfTitleUserFilters.length > 1) {
            titleDropdown = this.renderDropdown("job-titles",
                this.dictionary.translate("JOB_PREVIEW_TITLE_DROPDOWN"),
                titleDropdownPlaceholder,
                this.titleUserFilters.map(title => dataSelectOption(title.label))
            );
        }

        if (!locationDropdown && !titleDropdown) {
            return null;
        }

        return html`
            <div class="filter-dropdowns">
                ${locationDropdown}
                ${titleDropdown}
            </div>`;
    }

    private renderDropdown(id: string, label: string, placeholder: string = "", options: DataSelectOption[] = []): TemplateResult {
        return html`
            <div class="dropdown-container">
                <eop-dropdown id=${id} .inputId=${id} .label=${label} .placeholder=${placeholder} .options=${options}></eop-dropdown>
            </div>
        `;
    }

    private renderResultsArea(): Nullable<TemplateResult> {
        if (this.total === 0) {
            return html`
                <div class="empty-results">
                    ${this.resolveSlot(this.emptyResultsTextSlotName)}
                </div>
            `;
        }

        if (this.jobs.isEmpty()) {
            return null;
        }

        return html`
            <div class="items">${(this.renderEntries())}</div>`;
    }

    private renderEntries(): TemplateResult[] {
        return this.jobs.map(entry => this.renderEntry((entry)));
    }

    private renderEntry(job: Job): TemplateResult {
        return html`
            <div class="item">
                <a class="linked-job-title link"
                   href=${job.url}
                   target="_blank"
                   ${trackingTextlinkInteract(job.title, job.url)}
                >${job.title}</a>
                <div class="snippet">${this.jobSnippet(job)}</div>
            </div>
        `;
    }

    private jobSnippet(job: Job): string {
        return this.dictionary.translate("JOB_PREVIEW_SNIPPET_PATTERN", undefined, {
            "[COMPANY]": job.company,
            "[LOCATION]": job.locations.join(", ")
        });
    }

    private renderLoadMoreButton(): Nullable<TemplateResult> {
        const fetchMoreLabel = this.dictionary.translate("JOB_PREVIEW_FETCH_MORE");
        if (this.jobs.length < (this.total ?? 0)) {
            return html`
                <button class="load-more-button" @click=${this.fetchMoreHandler} ${trackingButtonInteract(fetchMoreLabel)}>
                    ${fetchMoreLabel}
                </button>
            `;
        }
        return null;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        void schedule(this.fetchSpinner.spinWhile(this.fetch()))
            .as("fetch");
    }

    protected updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);

        const locationDropdown = this.querySelector<EopDropdown>("#job-locations");
        if (locationDropdown) {
            this.locationDropdownUpdated(locationDropdown);
        }
        const titleDropdown = this.querySelector<EopDropdown>("#job-titles");
        if (titleDropdown) {
            this.titleDropdownUpdated(titleDropdown);
        }
    }

    private locationDropdownUpdated(locationDropdown: Element): void {
        prepareEvents(locationDropdown)
            .boundTo(fresh(locationDropdown, "locationDropdown"))
            .on(DROPDOWN_EVENT, (event: Event) => {
                const eventDetails = (event as DropdownEvent).detail;
                const value = eventDetails.value;

                this.currentLocationUserFilter = value === ""
                    ? null
                    : [value];
                this.offset = 0;
                void schedule(this.fetchMoreSpinner.spinWhile(this.fetch()))
                    .as("filter by location");
            });
    }

    private titleDropdownUpdated(titleDropdown: Element): void {
        prepareEvents(titleDropdown)
            .boundTo(fresh(titleDropdown, "titleDropdown"))
            .on(DROPDOWN_EVENT, (event: Event) => {
                const eventDetails = (event as DropdownEvent).detail;
                const value = eventDetails.value;

                this.currentTitleUserFilter = this.titleUserFilters.find(filter => filter.label === value) ?? null;
                this.offset = 0;
                void schedule(this.fetchMoreSpinner.spinWhile(this.fetch()))
                    .as("filter by title");
            });
    }

    private fetchMoreHandler(): void {
        this.offset += this.limit;
        void schedule(this.fetchMoreSpinner.spinWhile(this.fetch(true)))
            .as("more");
    }

    private async fetch(loadMore: boolean = false): Promise<void> {
        const invertTitleFilter = this.currentTitleUserFilter?.inverseFilter ?? false;
        const titleUserFilter = invertTitleFilter
            ? this.unionOfTitleUserFilters
            : (this.currentTitleUserFilter as ConfiguredUserFilter)?.filter;

        const response = await this.jobService.jobs(
            new JobPreviewRequest(
                this.typeFilter,
                this.currentLocationUserFilter ?? this.locationFilter,
                this.companiesFilter,
                this.titlePreFilter,
                titleUserFilter ?? [],
                this.limit,
                this.offset,
                invertTitleFilter
            )
        );

        this.jobs = loadMore
            ? this.jobs.concat(response.jobs)
            : response.jobs;

        this.total = response.total;
    }
}